import { useState } from 'react';
import { Link } from 'react-router-dom';
import "./faq.css";
import LoginModal from "../components/LoginModal";
import RegisterModal from "../components/RegisterModal";
import SEO from "../components/SEO";

const FAQ_SECTIONS = [
    {
        title: "Fundamentals",
        items: [
            {
                q: "What can I do with 'elloGovna?",
                a: "Track legislation in real time, search a directory of legislators and agency staff across the State of California, build your professional network, follow curated policy news, post announcements to the Sacramento policy community, and engage stakeholders to take action — all from a personalized dashboard.",
            },
            {
                q: "Do I need to pay to use 'elloGovna?",
                a: "No. If you're an employee of the State of California and have a ca.gov email or have an email with a domain registered on Cal-Access, you can register a free account. If you work in and with the State of California but are having trouble registering, reach out to ",
                linkTo: "mailto:info@ellogovna.com",
                linkText: "info@ellogovna.com",
                aAfter: " to request access.",
            },
            {
                q: "What can I do with a free account?",
                a: "We want 'elloGovna to be a valuable resource to all who work on public policy. Users with a free account have access to the Public Square, Directory, Dashboard, and Profiles. \n\nInterested in what features are included with subscriptions? Find out more below.",
            },
            {
                q: "What is the Directory?",
                a: "A searchable directory of legislators, committee staff, agency personnel, advocates, and many others working in the Sacramento policymaking community. Find the people and organizations you need to by name or issue area, so you always know who to reach out to.",
            },
            {
                q: "What is the Public Square?",
                a: "Hear ye, hear ye! Find and scroll a list of the day's headlines related to California policy issues. Also, scroll and engage a shared feed where subscribers post press events, fundraisers, office news, and other announcements visible to the entire Sacramento policy community.",
            },
            {
                q: "How does bill tracking work?",
                a: "Search and filter legislation, save the bills you care about, and follow amendments and status changes as they happen. Incorporate key contact information into your bill management system. Subscribers on a Workspace plan can also assign bills to team members, set position and priority, manage tasks for yourself and team members, and tag bills by subject.",
            },
        ],
    },
    {
        title: "Access & Subscriptions",
        items: [
            {
                q: "What subscription plans are available?",
                a: "An Individual plan for solo use (dashboard, legislation tracking, bill profiles, staff directory, Public Square, and job openings), and a Workspace plan for teams, which adds shared bill tracking, client and stakeholder management, team admin controls, and collaborative government relations workflows. See the ",
                linkTo: "/plans",
                linkText: "Plans & Pricing page",
                aAfter: " for full pricing.",
            },
            {
                q: "Is there a free trial?",
                a: "Yes, every plan includes a 14-day free trial with full access, and you won't be charged until the trial ends.",
            },
            {
                q: "Who is eligible to join?",
                a: "Anyone who works for or does business with the State of California, including lobbyists and consultants, legislative and agency staff, government relations teams, public affairs and advocacy professionals, nonprofits and associations, and board members or officers.",
            },
            {
                q: "What access levels exist within a Workspace?",
                a: "A Workspace has four access levels. Owner (the member who created it) can do everything below plus rename or delete the Workspace and grant Admin/Editor access. Admin can manage other members' permissions, manage Clients/Stakeholder Groups, and assign bills to anyone. Editor can track bills, send Client/Stakeholder feedback requests, and manage tasks on bills they lead. Viewer, the default for new members, can view tracked bills, tasks, reports, and Clients/Stakeholder Groups.",
            },
            {
                q: "How is billing handled?",
                a: "Subscriptions are managed through Lemon Squeezy, and you can cancel anytime from your billing portal.",
            },
        ],
    },
    {
        title: "Engaging Clients & Stakeholders",
        items: [
            {
                q: "How do Workspace subscribers engage clients and stakeholders?",
                a: "A Workspace is a shared account for your team to track bills together, assign work, collect feedback from Clients or Stakeholder Groups, and build reports all in one place. Clients and Stakeholder Groups (and their contacts) are added and managed right inside the Workspace.",
            },
            {
                q: "How do I send bills to a client for feedback?",
                a: "Send a set of bills to a Client or Stakeholder Group's contacts with a deadline, and their notes on each bill come straight back into the Workspace.",
            },
            {
                q: "Can I share a bill report with someone who does not have a login?",
                a: "Yes! You can build a bill report for a Client/Stakeholder Group (or for internal use) and send it to recipients, who view it through a link with no login required.",
            },
            {
                q: "Can my whole team search and filter the same way?",
                a: "Sure can! Saved Filters on the Legislation page can be shared with the whole Workspace, so everyone searches consistently, and they stay with the Workspace even if the member who created them leaves.",
            },
            {
                q: "Can I assign bills and tasks to specific team members?",
                a: "Indeed! Assign a Lead and other members to any bill the Workspace or its Clients/Stakeholder Groups are tracking, and track every task across every bill in one rolled-up, filterable view.",
            },
        ],
    },
];

const FAQ_JSON_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_SECTIONS.flatMap((section) =>
        section.items.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: `${item.a}${item.linkText || ""}${item.aAfter || ""}`,
            },
        }))
    ),
};

function FAQItem({ q, a, linkTo, linkText, aAfter, isOpen, onToggle }) {
    return (
        <div className={`faq-item ${isOpen ? "faq-item--open" : ""}`}>
            <button className="faq-question" onClick={onToggle} aria-expanded={isOpen}>
                <span>{q}</span>
                <i className={`bi ${isOpen ? "bi-dash-lg" : "bi-plus-lg"}`} />
            </button>
            {isOpen && (
                <div className="faq-answer">
                    <p>
                        {a}
                        {linkTo && <Link to={linkTo}>{linkText}</Link>}
                        {aAfter}
                    </p>
                </div>
            )}
        </div>
    );
}

export default function FAQ() {
    const [openKey, setOpenKey] = useState("Features-0");

    const toggle = (key) => {
        setOpenKey((prev) => (prev === key ? null : key));
    };

    return (
        <>
            <SEO
                title="FAQ | 'elloGovna"
                description="Answers to common questions about 'elloGovna: bill tracking, the Directory, Public Square, Workspace access levels, pricing, and how teams engage Clients and Stakeholder Groups."
                path="/faq"
                jsonLd={FAQ_JSON_LD}
            />
            <section className="faq-hero">
                <div className="faq-container">
                    <p className="faq-hero-eyebrow">Help Center</p>
                    <h1 className="faq-hero-title">Frequently Asked Questions</h1>
                    <p className="faq-hero-sub">
                        Answers about <i>'ello</i>Govna&rsquo;s features, subscription plans, and how teams use
                        the platform to engage clients and stakeholders.
                    </p>
                </div>
            </section>

            <section className="faq-content">
                <div className="faq-container">
                    {FAQ_SECTIONS.map((section) => (
                        <div className="faq-section" key={section.title}>
                            <h2 className="faq-section-title">{section.title}</h2>
                            <div className="faq-list">
                                {section.items.map((item, i) => {
                                    const key = `${section.title}-${i}`;
                                    return (
                                        <FAQItem
                                            key={key}
                                            {...item}
                                            isOpen={openKey === key}
                                            onToggle={() => toggle(key)}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="faq-cta">
                <div className="faq-container faq-cta-inner">
                    <h2 className="faq-cta-title">Still have questions?</h2>
                    <p className="faq-cta-body">
                        Reach out to <a href="mailto:info@ellogovna.com">info@ellogovna.com</a> and we&rsquo;ll
                        be happy to help.
                    </p>
                    <button
                        className="faq-btn faq-btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#registerModal"
                    >
                        Create Your Account
                    </button>
                </div>
            </section>

            <footer className="faq-footer">
                <span>&copy; <i>'ello</i>Govna {new Date().getFullYear()}</span>
                <a
                    className="faq-footer-social"
                    href="https://www.linkedin.com/company/ellogovna/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="'elloGovna on LinkedIn"
                >
                    <i className="bi bi-linkedin" />
                </a>
            </footer>

            <LoginModal />
            <RegisterModal />
        </>
    );
}
